import React, { useState } from 'react';
import { EmailShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';

import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';

import { colours } from 'src/constants/colours';
import { routePathNewsWithPostId } from 'src/constants/router';

import copyToClipboard from 'src/utils/copyToClipboard';

import useAlerts from 'src/hooks/useAlerts';
import type { ModalProps } from 'src/hooks/useModalOpenState';
import { useAddContentEventMutation, useGenerateAppLinkQuery } from 'src/hooks/__generated__/queries';
import { styledComponent, styledTag } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent, EventParamKeys, Methods } from 'src/utils/analytics';
import type { AppLinkParamsInput } from 'src/types/__generated__/graphql';
import { AppLinkType } from 'src/types/__generated__/graphql';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialogContent-root {
        padding: 0 1.5rem;
        padding-bottom: 2.5rem;
    }
    .MuiDialogActions-root {
        padding: 0 1.5rem 1.5rem 1.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0 1.5rem;
    }
`;

const Link = styledTag('div')`
    margin-top: 1.4375rem;
    margin-bottom: 1.25rem;
`;

const Divider = styledTag('hr')`
    margin-top: 1.75rem;
    margin-bottom: 1.875rem;
    border-top: 0.0625rem solid ${colours.black.O16};
`;

const ButtonsContainer = styledComponent(Box)``;
ButtonsContainer.defaultProps = {
    display: 'flex',
    justifyContent: 'space-evenly',
};

const ButtonContainer = styledComponent(Box)`
    button {
        border: 0.0625rem solid ${colours.shareButtonBorder};
        box-sizing: border-box;
        border-radius: 6.25rem;
        width: 3rem;
        height: 3rem;
        padding: 0.75rem;
        background: transparent;
        cursor: pointer;
        margin-bottom: 0.75rem;

        transition: border 200ms ease-in-out, background-color 200ms ease-in-out;

        &:hover {
            border-color: ${colours.primary.O60};
            background-color: ${colours.primary.O16};
        }
    }
`;
ButtonContainer.defaultProps = {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
};

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};

export type SimpleShareContentProps = {
    type: 'news-post' | 'ad' | 'estimate' | 'company' | 'company-pagesjaunes' | 'user' | 'post';
    id: string;
};

export type GalleryInvitationShareContentProps = {
    type: 'galleryInvitation';
    id: string;
    invitationId: string;
};

type ShareContentProps = SimpleShareContentProps | GalleryInvitationShareContentProps;
interface Props extends ModalProps {
    title?: string;
    externalShareTitle: string;
    shareContentProps: ShareContentProps;
}

export const ExternalShareModal: React.FC<Props> = ({
    open,
    onClose,
    title,
    externalShareTitle,
    shareContentProps,
}) => {
    const { t } = useTranslation(['ad', 'common']);
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();
    const [externalUrl, setExternalUrl] = useState(getDefaultExternalUrl(shareContentProps));

    useGenerateAppLinkQuery({
        variables: {
            type: shareContentPropsToAppLinkType(shareContentProps),
            params: shareContentPropsToAppLinkParams(shareContentProps),
        },
        skip: !open,
        onCompleted(data) {
            if (data.generateAppLink?.shortUrl) {
                setExternalUrl(data.generateAppLink.shortUrl);
            }
        },
    });
    const [addContentEventMutation] = useAddContentEventMutation();

    const addExternalSharedEvent = async () => {
        if (['news-post', 'ad', 'estimate', 'company', 'post'].includes(shareContentProps.type)) {
            await addContentEventMutation({
                variables: {
                    ...(shareContentProps.type === 'news-post' && { newsPostId: shareContentProps.id }),
                    ...(['ad', 'estimate'].includes(shareContentProps.type) && { adId: shareContentProps.id }),
                    ...(shareContentProps.type === 'company' && { companyId: shareContentProps.id }),
                    ...(shareContentProps.type === 'post' && { postId: shareContentProps.id }),
                    eventType: 'shared_externally',
                },
                fetchPolicy: 'no-cache',
            });
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <DialogTitle onClose={onClose}>
                <Typography variant="h2">{title ?? t('common:shareVia')}</Typography>
            </DialogTitle>

            <DialogContent>
                <Link>{externalUrl}</Link>

                <Button
                    size="small"
                    startIcon={<Icon size="1.25rem" name="Link" />}
                    onClick={async () => {
                        logEventDynamic(AnalyticsEvent.ShareViaCopyLink);
                        try {
                            await copyToClipboard(externalUrl);
                            addAlert({
                                type: 'success',
                                message: t('ad:linkCopied'),
                            });
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- this promise is simply for tracking user behaviour, should not wait
                            addExternalSharedEvent();
                        } catch (error: unknown) {}
                    }}
                >
                    {t('common:share.copyLink')}
                </Button>

                <Divider />

                <ButtonsContainer>
                    <ButtonContainer
                        onClick={() => {
                            logEventDynamic(AnalyticsEvent.ShareViaEmail, {
                                [EventParamKeys.Method]: Methods.Email,
                            });
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- unnecessary
                            addExternalSharedEvent();
                        }}
                    >
                        <EmailShareButton url={externalUrl} subject={externalShareTitle} resetButtonStyle={false}>
                            <Icon size="1.4rem" name="Mail" />
                        </EmailShareButton>
                        {t('common:share.email')}
                    </ButtonContainer>

                    <ButtonContainer>
                        <WhatsappShareButton
                            url={externalUrl}
                            title={externalShareTitle}
                            resetButtonStyle={false}
                            onClick={() => {
                                logEventDynamic(AnalyticsEvent.ShareViaWhatsApp, {
                                    [EventParamKeys.Method]: Methods.WhatsApp,
                                });
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises -- unnecessary
                                addExternalSharedEvent();
                            }}
                        >
                            <Icon size="1.4rem" name="Whatsapp" />
                        </WhatsappShareButton>
                        {t('common:share.whatsapp')}
                    </ButtonContainer>

                    <ButtonContainer>
                        {/* Won't work with localhost urls */}
                        <LinkedinShareButton
                            url={externalUrl}
                            title={externalShareTitle}
                            source={process.env.NEXT_PUBLIC_SITE_URL}
                            resetButtonStyle={false}
                            onClick={() => {
                                logEventDynamic(AnalyticsEvent.ShareViaLinkedIn, {
                                    [EventParamKeys.Method]: Methods.LinkedIn,
                                });
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises -- unnecessary
                                addExternalSharedEvent();
                            }}
                        >
                            <Icon size="1.3rem" name="Linkedin" />
                        </LinkedinShareButton>
                        {t('common:share.linkedin')}
                    </ButtonContainer>
                </ButtonsContainer>
            </DialogContent>
        </StyledDialog>
    );
};

const shareContentPropsToAppLinkType = (shareContentProps: ShareContentProps): AppLinkType => {
    switch (shareContentProps.type) {
        case 'ad':
        case 'estimate':
            return AppLinkType.Ad;
        case 'company':
            return AppLinkType.Company;
        case 'company-pagesjaunes':
            return AppLinkType.Company;
        case 'user':
            return AppLinkType.User;
        case 'post':
            return AppLinkType.NewsFeedPost;
        case 'news-post':
            return AppLinkType.NewsPost;
        case 'galleryInvitation':
            return AppLinkType.Gallery;
        default:
            throw new Error('Unknown content type');
    }
};

const shareContentPropsToAppLinkParams = (shareContentProps: ShareContentProps): AppLinkParamsInput[] => {
    switch (shareContentProps.type) {
        case 'galleryInvitation': {
            return [
                { name: 'id', value: shareContentProps.id },
                { name: 'invitationId', value: shareContentProps.invitationId },
            ];
        }
        default: {
            return [{ name: 'id', value: shareContentProps.id }];
        }
    }
};

const getDefaultExternalUrl = (shareContentProps: ShareContentProps): string => {
    switch (shareContentProps.type) {
        case 'galleryInvitation':
            return `${process.env.NEXT_PUBLIC_SITE_URL}/galleries/${shareContentProps.id}?invitationId=${shareContentProps.invitationId}`;
        case 'post':
            return `${process.env.NEXT_PUBLIC_SITE_URL}${routePathNewsWithPostId}${shareContentProps.id}`;
        default:
            return `${process.env.NEXT_PUBLIC_SITE_URL}/${shareContentProps.type}/${shareContentProps.id}`;
    }
};
